import { useEffect, useMemo } from 'react';
import { hasGlobalTerritoriesApplied } from '@helpers/territories';
import FilterTypes from '../../../../constants/FilterTypes.ts';
import { INITIAL_FILTERS } from '../constants';
import { actionTypes } from '../reducer';

const isFilterValuePopulated = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  return typeof value === 'string' && value !== '';
};

const useFilters = ({
  filters,
  dispatch,
  workOrderTypesForSelect,
  workOrderStatusesForSelect,
  subcontractorsForSelect,
  visitStatusesForSelect,
  buildingId,
  inspectionId,
  accountId,
  subcontractorsEnabled
}) => {
  const globalTerritoriesApplied = hasGlobalTerritoriesApplied();
  const hasPrimaryTerritoryScope = globalTerritoriesApplied || isFilterValuePopulated(filters.values.territory);

  useEffect(() => {
    if (!hasPrimaryTerritoryScope && isFilterValuePopulated(filters.values.additionalTerritory)) {
      dispatch({ type: actionTypes.FILTER_CHANGED, value: '', field: 'additionalTerritory' });
    }
  }, [hasPrimaryTerritoryScope, filters.values.additionalTerritory, dispatch]);

  const onChangeFilter = (fieldName) => (value) => {
    dispatch({ type: actionTypes.FILTER_CHANGED, value, field: fieldName });
  };

  const onChangeTechnicianTeamFilter = (value) => {
    dispatch({ type: actionTypes.FILTER_CHANGED, value, field: 'technicianTeam' });
    dispatch({ type: actionTypes.FILTER_CHANGED, value: INITIAL_FILTERS.values.technician, field: 'technician' });
  };

  const onChangeTechnicianFilter = (value) => {
    dispatch({ type: actionTypes.FILTER_CHANGED, value, field: 'technician' });
    dispatch({
      type: actionTypes.FILTER_CHANGED,
      value: INITIAL_FILTERS.values.technicianTeam,
      field: 'technicianTeam'
    });
  };

  const onToggleFilter = (fieldName) => () => {
    dispatch({ type: actionTypes.FILTER_TOGGLED, field: fieldName });
  };

  const onChangeDateFilter = (fieldName) => (dateFieldName, value) => {
    dispatch({
      type: actionTypes.DATE_RANGE_FILTER_CHANGED,
      field: fieldName,
      dateField: dateFieldName,
      value
    });
  };

  const filtersList = useMemo(() => {
    const list = [
      {
        field: 'technicianTeam',
        locale: 'technician_team',
        type: FilterTypes.GenericMultiSelect,
        active: filters.active.technicianTeam,
        value: filters.values.technicianTeam,
        onChange: onChangeTechnicianTeamFilter,
        onToggle: onToggleFilter('technicianTeam')
      },
      {
        field: 'technician',
        locale: 'technician',
        type: FilterTypes.GenericMultiSelect,
        active: filters.active.technician,
        value: filters.values.technician,
        onChange: onChangeTechnicianFilter,
        onToggle: onToggleFilter('technician'),
        withUnassigned: true,
        allowLetterAvatar: true,
        disableSorting: true
      },
      {
        field: 'workOrderType',
        locale: 'type',
        type: FilterTypes.GenericMultiSelect,
        active: filters.active.workOrderType,
        value: filters.values.workOrderType,
        onChange: onChangeFilter('workOrderType'),
        onToggle: onToggleFilter('workOrderType'),
        optionsForSelect: workOrderTypesForSelect
      },
      {
        field: 'status',
        locale: 'work_order_status',
        type: FilterTypes.GenericMultiSelect,
        active: filters.active.status,
        value: filters.values.status,
        onChange: onChangeFilter('status'),
        onToggle: onToggleFilter('status'),
        optionsForSelect: workOrderStatusesForSelect
      },
      {
        field: 'visitDate',
        locale: 'visit_date',
        type: 'dateRangePicker',
        active: filters.active.visitDate,
        values: filters.values.visitDate,
        onChange: onChangeDateFilter('visitDate'),
        onToggle: onToggleFilter('visitDate')
      },
      {
        field: 'dueDate',
        locale: 'due_date',
        type: 'dateRangePicker',
        active: filters.active.dueDate,
        values: filters.values.dueDate,
        onChange: onChangeDateFilter('dueDate'),
        onToggle: onToggleFilter('dueDate')
      },
      {
        field: 'visitStatus',
        locale: 'visit_status',
        type: FilterTypes.GenericMultiSelect,
        active: filters.active.visitStatus,
        value: filters.values.visitStatus,
        onChange: onChangeFilter('visitStatus'),
        onToggle: onToggleFilter('visitStatus'),
        optionsForSelect: visitStatusesForSelect
      },
      {
        field: 'woNumber',
        locale: 'wo',
        type: 'textBox',
        active: filters.active.woNumber,
        value: filters.values.woNumber,
        onChange: onChangeFilter('woNumber'),
        onToggle: onToggleFilter('woNumber')
      },
      {
        field: 'tags',
        backendField: 'tags',
        locale: 'work_order_tags',
        type: FilterTypes.GenericMultiSelect,
        active: filters.active.tags,
        value: filters.values.tags,
        onChange: onChangeFilter('tags'),
        onToggle: onToggleFilter('tags')
      },
      {
        field: 'building',
        locale: 'building',
        type: buildingId || inspectionId ? 'hidden' : FilterTypes.GenericMultiSelect,
        active: filters.active.building,
        value: filters.values.building,
        onChange: onChangeFilter('building'),
        onToggle: onToggleFilter('building')
      },
      {
        field: 'buildingStatus',
        locale: 'building_status',
        type: buildingId || inspectionId ? 'hidden' : 'booleanSelect',
        active: filters.active.buildingStatus,
        value: filters.values.buildingStatus,
        onChange: onChangeFilter('buildingStatus'),
        onToggle: onToggleFilter('buildingStatus')
      },
      {
        field: 'referenceNumber',
        locale: 'reference_number',
        type: 'textBox',
        active: filters.active.referenceNumber,
        value: filters.values.referenceNumber,
        onChange: onChangeFilter('referenceNumber'),
        onToggle: onToggleFilter('referenceNumber')
      },
      {
        field: 'account',
        locale: 'account',
        type: 'hidden',
        active: !!accountId,
        value: filters.values.account,
        onChange: onChangeFilter('account'),
        onToggle: onToggleFilter('account')
      }
    ];

    if (!globalTerritoriesApplied) {
      list.push({
        field: 'territory',
        locale: 'territory',
        type: buildingId || inspectionId ? 'hidden' : FilterTypes.GenericMultiSelect,
        active: filters.active.territory,
        value: filters.values.territory,
        withUnassigned: true,
        onChange: onChangeFilter('territory'),
        onToggle: onToggleFilter('territory')
      });
    }

    list.push({
      field: 'additionalTerritory',
      backendField: 'additional_territory',
      locale: 'additional_territory',
      type: hasPrimaryTerritoryScope ? FilterTypes.GenericMultiSelect : FilterTypes.Hidden,
      active: filters.active.additionalTerritory,
      value: filters.values.additionalTerritory,
      onChange: onChangeFilter('additionalTerritory'),
      onToggle: onToggleFilter('additionalTerritory')
    });

    if (subcontractorsEnabled) {
      list.push({
        field: 'subcontractor',
        locale: 'subcontractor',
        type: FilterTypes.GenericMultiSelect,
        active: filters.active.subcontractor,
        value: filters.values.subcontractor,
        onChange: onChangeFilter('subcontractor'),
        onToggle: onToggleFilter('subcontractor'),
        optionsForSelect: subcontractorsForSelect
      });
    }
    return list;
  }, [
    filters,
    dispatch,
    buildingId,
    inspectionId,
    accountId,
    subcontractorsEnabled,
    subcontractorsForSelect,
    globalTerritoriesApplied
  ]);

  return filtersList;
};

export default useFilters;
